import { useState } from 'react';

import { Box, Chip, Grid, Paper, Skeleton } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from 'react-query';
import DateChip from 'src/components/DateChip';
import Subtitle from 'src/components/Subtitle';
import { getFiagroAssetData } from 'src/services/operation/operationFiagro';
import { FiagroAssetRaw } from 'src/types/operations/fiagro';
import { convertValue } from 'src/utils/number';

import { formatDate } from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip);

type CompositionMode = 'segmento' | 'inadimplencia';

const CATEGORY_ENTRIES: { key: keyof FiagroAssetRaw; label: string }[] = [
  { key: 'valorCategoriaImobiliario', label: 'Imobiliário' },
  { key: 'valorCategoriaParticipacoes', label: 'Participações' },
  { key: 'valorCategoriaRendaFixa', label: 'Renda Fixa' },
  { key: 'valorCategoriaDireitosCreditorios', label: 'Direitos Creditórios' },
  { key: 'valorCategoriaCotas', label: 'Cotas' },
  { key: 'valorCategoriaCarbono', label: 'Carbono' },
];

const DELINQUENCY_ENTRIES: { key: keyof FiagroAssetRaw; label: string }[] = [
  { key: 'vencidosAte30Dias', label: 'Até 30 dias' },
  { key: 'vencidos31A60Dias', label: '31 a 60 dias' },
  { key: 'vencidos61A90Dias', label: '61 a 90 dias' },
  { key: 'vencidos91A120Dias', label: '91 a 120 dias' },
  { key: 'vencidos121A180Dias', label: '121 a 180 dias' },
  { key: 'vencidos181A360Dias', label: '181 a 360 dias' },
  { key: 'vencidos361A720Dias', label: '361 a 720 dias' },
  { key: 'vencidos721A1080Dias', label: '721 a 1080 dias' },
  { key: 'vencidosAcima1080Dias', label: 'Acima de 1080 dias' },
];

export default function AssetComposition({ hash }: { hash: string }) {
  const [compositionMode, setCompositionMode] =
    useState<CompositionMode>('segmento');

  const { data, isLoading } = useQuery({
    queryKey: ['operationFIAGROAssetRecent', hash],
    queryFn: () => getFiagroAssetData(hash, 1),
  });

  const mostRecent = data?.[0];
  const maxDate = mostRecent?.dataReferencia ?? '';

  const entries =
    compositionMode === 'segmento' ? CATEGORY_ENTRIES : DELINQUENCY_ENTRIES;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const compositionOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item: { raw: unknown }) => convertValue(item.raw as number),
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: (value: number) => convertValue(value) },
      },
    },
    maintainAspectRatio: false,
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          variant="text"
          width={300}
          height={20}
          sx={{ mb: 1, mt: 3 }}
        />
        <Box sx={{ height: 2, backgroundColor: 'black', mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={360} />
      </Box>
    );
  }

  return (
    <>
      <Subtitle title="Composição da Carteira de Ativos" />
      <Paper square sx={{ padding: '24px 18px', mb: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection="column"
          mb={2}>
          <Box width="100%" display="flex" justifyContent="flex-end">
            <DateChip
              title="Competência"
              date={maxDate ? formatDate(maxDate) : 'N/D'}
            />
          </Box>
          <Grid container spacing={1} justifyContent="center">
            <Grid item>
              <Chip
                label="Segmento"
                color={compositionMode === 'segmento' ? 'primary' : 'default'}
                variant={compositionMode === 'segmento' ? 'filled' : 'outlined'}
                onClick={() => setCompositionMode('segmento')}
                sx={{ cursor: 'pointer' }}
              />
            </Grid>
            <Grid item>
              <Chip
                label="Faixa de Inadimplência"
                color={
                  compositionMode === 'inadimplencia' ? 'primary' : 'default'
                }
                variant={
                  compositionMode === 'inadimplencia' ? 'filled' : 'outlined'
                }
                onClick={() => setCompositionMode('inadimplencia')}
                sx={{ cursor: 'pointer' }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ height: entries.length * 50 + 40 }}>
          <Bar
            data={{
              labels: entries.map((e) => e.label),
              datasets: [
                {
                  label: 'Valor',
                  data: entries.map(
                    ({ key }) => (mostRecent?.[key] as number) ?? 0,
                  ),
                  backgroundColor: '#FF8211',
                },
              ],
            }}
            options={compositionOptions}
          />
        </Box>
      </Paper>
    </>
  );
}
